/**
 * Unified currency conversion helpers (pure functions).
 */
import type { CurrencyBalances } from "types/Balance";
import type { TransactionLike } from "shared/money/balances";
import {
    autoConversionRatesForDisplay,
    defaultConversionRatesForDisplay,
    DEFAULT_DISPLAY_CURRENCY,
} from "shared/money/currencyDefaults";
import {
    conversionRatesForDate,
    getCachedBolsaHistory,
} from "shared/money/historicalFxRates";
import type { DollarQuote } from "features/market";
import { convertBalanceToDisplayCurrency, mergeConversionRates, toDisplayAmount } from "shared/money/fx";

export interface ConversionOptions {
    /** Target currency; defaults to display currency. */
    toCurrency?: string;
    /** When set, historical bolsa rates are used for that date. */
    date?: string | Date;
}

export interface CurrencyConversionEngine {
    displayCurrency: string;
    currentRates: Record<string, number>;
    history: DollarQuote[];
    fallbackUsdArs?: number;
}

function resolveTargetCurrency(engine: CurrencyConversionEngine, toCurrency?: string): string {
    return String(toCurrency ?? engine.displayCurrency).trim().toUpperCase() || engine.displayCurrency;
}

/**
 * Resolves the rate map for a conversion: historical when `date` is provided, else current auto rates.
 */
export function resolveConversionRates(
    engine: CurrencyConversionEngine,
    options?: Pick<ConversionOptions, "date" | "toCurrency">,
): Record<string, number> {
    const target = resolveTargetCurrency(engine, options?.toCurrency);
    if (options?.date) {
        const historical = conversionRatesForDate(
            target,
            engine.history.length > 0 ? engine.history : getCachedBolsaHistory(),
            options.date,
            engine.fallbackUsdArs,
        );
        if (historical) return historical;
    }
    if (target === engine.displayCurrency) {
        return engine.currentRates;
    }
    const usdArs = engine.fallbackUsdArs;
    if (usdArs != null && Number.isFinite(usdArs) && usdArs > 0) {
        return autoConversionRatesForDisplay(target, usdArs);
    }
    return defaultConversionRatesForDisplay(target);
}

export function convertAmountWithEngine(
    engine: CurrencyConversionEngine,
    amount: number,
    fromCurrency: string,
    options?: ConversionOptions,
): number {
    const target = resolveTargetCurrency(engine, options?.toCurrency);
    const from = String(fromCurrency ?? "").trim().toUpperCase();
    if (!from || from === target) return amount;
    const rates = resolveConversionRates(engine, options);
    return toDisplayAmount(amount, from, target, rates);
}

export function convertBalancesWithEngine(
    engine: CurrencyConversionEngine,
    balances: CurrencyBalances,
    options?: ConversionOptions,
): number {
    const target = resolveTargetCurrency(engine, options?.toCurrency);
    const rates = resolveConversionRates(engine, options);
    return convertBalanceToDisplayCurrency(balances, target, rates);
}

export interface DatedAmount {
    amount: number;
    currency: string;
    date?: string | Date;
}

/** Per-transaction user balance contributions for historical FX aggregation. */
export function calcUserDatedBalanceContributions(
    txs: ReadonlyArray<TransactionLike & { date?: string }>,
    user: string,
): DatedAmount[] {
    const items: DatedAmount[] = [];
    for (const tx of txs) {
        const currency = tx?.currency || "ARS";
        const userShare = tx?.splitDetails?.[user] ?? 0;
        const contribution = tx?.payer === user ? ((tx?.amount || 0) - userShare) : (-userShare);
        if (contribution === 0) continue;
        items.push({ amount: contribution, currency, date: tx.date });
    }
    return items;
}

/** Sums amounts, applying per-item historical rates when dates are present. */
export function convertDatedAmountsTotal(
    engine: CurrencyConversionEngine,
    items: ReadonlyArray<DatedAmount>,
    options?: Omit<ConversionOptions, "date">,
): number {
    const target = resolveTargetCurrency(engine, options?.toCurrency);
    let total = 0;
    for (const item of items) {
        total += convertAmountWithEngine(engine, item.amount, item.currency, {
            toCurrency: target,
            date: item.date,
        });
    }
    return total;
}

/** Builds engine from user settings (auto rates only). */
export function buildConversionEngine(params: {
    displayCurrency?: string;
    conversionRates?: Record<string, number>;
    usdArsReference?: number;
    history?: DollarQuote[];
}): CurrencyConversionEngine {
    const displayCurrency = String(params.displayCurrency ?? DEFAULT_DISPLAY_CURRENCY).trim().toUpperCase()
        || DEFAULT_DISPLAY_CURRENCY;
    return {
        displayCurrency,
        currentRates: mergeConversionRates(params.conversionRates, displayCurrency),
        history: params.history ?? getCachedBolsaHistory(),
        fallbackUsdArs: params.usdArsReference,
    };
}
